import {
  List,
  Title,
  Datagrid,
  TextField,
  useStore,
  ReferenceField,
  FunctionField,
  ListActions,
  EditButton,
  Button,
  useNotify,
  useRefresh,
  useDataProvider,
  SimpleList,
} from "react-admin";
import CustomFilter from "features/CustomFilter";
import useScheduleFilter from "features/filters/useScheduleFilter";
import { Box, Typography, Paper, useMediaQuery } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { MOBILE_BREAKPOINT, WeeklyScheduleStatuses, WeeklyScheduleStatusesTexts } from "utils/constants";
import CyclicScheduleExpanded from "./CyclicScheduleExpanded";
import CyclicScheduleMobileExpanded from "./CyclicScheduleMobileExpanded";
import { studentRequests } from "api/studentApi";
import SecuredComponent from "server/security/SecuredComponent";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const ListCyclicSchedules = () => {
  const STORE_KEY = "cyclicSchedule.filters";
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const schedule = useScheduleFilter({ filters, hasCourse: true });
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const handleApproveSchedule = async id => {
    await dataProvider.customRequest(
      studentRequests.updateCyclicScheduleStatus(id),
      { status: WeeklyScheduleStatuses.Approved },
      "PUT",
    );
    notify("Одобрихте успешно", { type: "success" });
    refresh();
  };

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  return (
    <>
      {!filters && <Title title={"Циклични дисциплини"} />}
      <CustomFilter
        title="Избери разписание"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={schedule.fields}
        buttonLabel="Покажи"
        buttonDisabled={!schedule.isComplete}
        buttonOnClick={() => handleSearch(schedule.query)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за разписание за циклични дисциплини .
            </Typography>
          </Box>
        </Paper>
      ) : (
        <List
          title="Циклични дисциплини"
          actions={
            <SecuredComponent permission={permissions.canManageCyclicSchedules}>
              <ListActions />
            </SecuredComponent>
          }
          exporter={false}
          filter={filters}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Поток", value: record?.name }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Специалност",
                        value: (
                          <ReferenceField
                            source="specialityId"
                            reference={administrationRequests.getSpecialitiesSelect()}
                            sortable={false}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <>
                  <div>
                    <MobileListRow fields={[{ label: "Учебни групи", value: record?.studyGroups }]} />
                    <MobileListRow
                      fields={[
                        {
                          label: "Статус",
                          value: WeeklyScheduleStatusesTexts[record.status] || "Неизвестен",
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Учебен план", value: record?.curriculumName }]} />
                    <MobileListRow
                      fields={[
                        {
                          label: "Език на обучение",
                          value: (
                            <ReferenceField
                              source="instructionLanguageId"
                              reference={administrationRequests.getInstructionLanguagesSelect()}
                              sortable={false}
                            >
                              <TextField source="name" />
                            </ReferenceField>
                          ),
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Курс", value: record?.course }]} />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <SecuredComponent permission={permissions.canChangeCyclicSchedulesStatus}>
                        {record.status === WeeklyScheduleStatuses.New && (
                          <ConfirmModal
                            titleContent="Одобряване на разписание"
                            onConfirm={() => handleApproveSchedule(record.id)}
                            message="Сигурни ли сте, че искате да одобрите това разписание?"
                            triggerButton={
                              <Button
                                startIcon={<CheckCircleIcon />}
                                size="small"
                                sx={{ margin: 0.5 }}
                                label="Одобри"
                              />
                            }
                          />
                        )}
                      </SecuredComponent>
                    </div>
                  </div>
                  <CyclicScheduleMobileExpanded record={record} />
                </>
              )}
              tertiaryText={() => (
                <SecuredComponent permission={permissions.canManageCyclicSchedules}>
                  <EditButton label="" />
                </SecuredComponent>
              )}
            />
          ) : (
            <Datagrid
              expandSingle={({ record }) => <CyclicScheduleExpanded record={record} />}
              expand={<CyclicScheduleExpanded />}
              rowClick={false}
              bulkActionButtons={false}
            >
              {/* //Поток */}
              <TextField
                source="name"
                label="Поток"
                sortable={false}
              />
              <TextField
                source="studyGroups"
                label="Учебни групи"
                sortable={false}
              />
              <FunctionField
                label="Статус"
                sortable={false}
                render={record => WeeklyScheduleStatusesTexts[record.status] || "Неизвестен"}
              />

              <ReferenceField
                source="specialityId"
                reference={administrationRequests.getSpecialitiesSelect()}
                label="Специалност"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>

              <TextField
                source="curriculumName"
                label="Учебен план"
                sortable={false}
              />
              <ReferenceField
                source="instructionLanguageId"
                reference={administrationRequests.getInstructionLanguagesSelect()}
                label="Език на обучение"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>

              <TextField
                label="Курс"
                source="course"
                sortable={false}
              />

              <SecuredComponent permission={permissions.canChangeCyclicSchedulesStatus}>
                <FunctionField
                  render={record =>
                    record.status === WeeklyScheduleStatuses.New && (
                      <ConfirmModal
                        titleContent="Одобряване на разписание"
                        onConfirm={() => handleApproveSchedule(record.id)}
                        message="Сигурни ли сте, че искате да одобрите това разписание?"
                        triggerButton={
                          <Button
                            startIcon={<CheckCircleIcon />}
                            size="small"
                            sx={{ margin: 0.5 }}
                            label="Одобри"
                          />
                        }
                      />
                    )
                  }
                />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canManageCyclicSchedules}>
                <EditButton />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListCyclicSchedules;
